/**
 * Composable per Users autocomplete con loading e error handling centralizzati
 *
 * Fornisce funzionalità di ricerca asincrona di utenti (ApplicationUser) con OData query building
 * e loading state automatico. Adatto per input autocomplete in form e dialog.
 *
 * @param options - Opzioni configurazione (withLoading injection per loading state)
 * @returns Oggetto con users reactive array e metodi di ricerca/pulizia
 */

import { ref, readonly } from 'vue';
import { useODataBuilder } from '../useODataBuilder';
import { useLoadingState } from '../useLoadingState';
import { useNotification } from '../useNotification';
import { useStaleSafeRequest } from '../useStaleSafeRequest';
import type { BaseAutocompleteComposableOptions } from './types';
import type { BaseAutocompleteOptions } from '@/lib/autocompletes';
import type { ApplicationUserAutocompleteResult } from '@/lib/models/applicationUser';
import { getUsersAutocomplete } from '@/lib/autocompletes/users';

export function useUsersAutocomplete(options: BaseAutocompleteComposableOptions = {}) {
	const users = ref<ApplicationUserAutocompleteResult[]>([]);

	const { buildOdataQuery } = useODataBuilder();
	const withLoadingFn = options.withLoading || useLoadingState().withLoading;
	const { error } = useNotification();
	const { requestToken } = useStaleSafeRequest();

	async function searchUsers(options: BaseAutocompleteOptions = {}): Promise<void> {
		const token = requestToken();
		await withLoadingFn(async () => {
			try {
				const result = await getUsersAutocomplete(buildOdataQuery, options);
				if (token.isLatest()) {
					users.value = result;
				}
			} catch (err) {
				console.error('❌ ERROR useUsersAutocomplete.searchUsers:', err);
				error('Errore durante il caricamento degli utenti! Ricaricare la pagina e riprovare.');
				if (token.isLatest()) {
					users.value = [];
				}
			}
		}, 'searchUsers');
	}

	function clearUsers(): void {
		users.value = [];
	}

	return {
		users: readonly(users),
		searchUsers,
		clearUsers,
	};
}
